import { useDispatch, useSelector } from 'react-redux';
import {
	getAdditionalServicesData,
	getAdditionalServicesLoading,
} from 'redux/reducers/emManagement/selectors';
import { useEffect, useState } from 'react';
import {
	createAdditionalServiceRequest,
	getAdditionalServicesRequest,
	updateAdditionalServiceRequest,
} from 'redux/reducers/emManagement/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { Box, Button, Typography } from '@mui/material';
import { TAdditionalService } from 'redux/reducers/emManagement/types';
import { TTableStructure } from 'ui/EasTable/types';
import { TBoxStructure } from 'components/EMManagement/EMManagementId/EasParamsBox/types';
import {
	getGeneralAttachments,
	getGeneralAttachmentsLoading,
} from 'redux/reducers/generalAttachments/selectors';
import { TGeneralAttachmentRow } from 'redux/reducers/generalAttachments/types';
import {
	addGeneralAttachmentVersionRequest,
	createGeneralAttachmentRequest,
	deleteGeneralAttachmentRequest,
	downloadGeneralAttachmentRequest,
	getGeneralAttachmentsRequest,
} from 'redux/reducers/generalAttachments/reducer';
import { EditParamsDialog } from './EditParamsDialog';
import { AddDocumentDialog } from './AddDocumentDialog';
import { TAddDocumentFormData } from './AddDocumentDialog/types';
import { UploadVersionDialog } from './UploadVersionDialog';
import { TUploadVersionFormData } from './UploadVersionDialog/types';
import { DownloadDocumentDialog } from './DownloadDocumentDialog';
import { tableStructure } from './tableStructure';
import { ConfirmationDialog } from '../../../ui/ConfirmationDialog';

export const GeneralContractDocuments = () => {
	const dispatch = useDispatch();
	const generalAttachments = useSelector(getGeneralAttachments);
	const generalAttachmentsLoading = useSelector(getGeneralAttachmentsLoading);
	useEffect(() => {
		dispatch(getGeneralAttachmentsRequest({ pageNumber: 1, pageSize: PAGE_SIZE }));
	}, [dispatch]);

	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};

	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const handleAddDialogClose = () => {
		setIsAddDialogOpen(false);
	};

	const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
	const [idToDelete, setIdToDelete] = useState<number | null>(null);
	const setCloseConfirmationDialog = () => {
		setOpenConfirmationDialog(false);
		setIdToDelete(null);
	};
	const handleConfirmationDialog = () => {
		if (idToDelete) {
			dispatch(
				deleteGeneralAttachmentRequest({
					attachmentId: idToDelete,
					listRequest: { pageNumber: 1, pageSize: PAGE_SIZE },
				}),
			);
		}
		setCloseConfirmationDialog();
	};

	const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
	const [idToUpload, setIdToUpload] = useState<number | null>(null);
	const handleUploadDialogClose = () => {
		setIsUploadDialogOpen(false);
		setIdToUpload(null);
	};
	const handleUploadVersion = (data: TUploadVersionFormData) => {
		if (!data.file || !idToUpload) return;
		dispatch(
			addGeneralAttachmentVersionRequest({
				attachmentId: idToUpload,
				file: data.file,
				comment: data.comment || null,
				listRequest: { pageNumber: 1, pageSize: PAGE_SIZE },
			}),
		);
		handleUploadDialogClose();
	};

	const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
	const [documentToDownload, setDocumentToDownload] = useState<TGeneralAttachmentRow | null>(null);
	const handleDownloadDialogClose = () => {
		setIsDownloadDialogOpen(false);
		setDocumentToDownload(null);
	};
	const handleDownloadVersion = (versionId: number) => {
		if (!documentToDownload) return;
		dispatch(
			downloadGeneralAttachmentRequest({
				attachmentId: versionId,
				fileName: documentToDownload.fileName,
			}),
		);
	};

	const handleAddDocument = (data: TAddDocumentFormData) => {
		if (!data.file) return;
		dispatch(
			createGeneralAttachmentRequest({
				file: data.file,
				typeId: data.typeId,
				comment: data.comment || null,
				listRequest: { pageNumber: 1, pageSize: PAGE_SIZE },
			}),
		);
		handleAddDialogClose();
	};

	const [idToEdit, setIdToEdit] = useState<number | null>(null);

	const handleSave = (data: Record<string, any>) => {
		console.log('idToEdit', idToEdit);
		if (idToEdit === null) {
			console.log('data', data);
			dispatch(
				createAdditionalServiceRequest({
					data: {
						name: data.name,
						price: Number(data.price),
						isRecurring: data.isRecurring,
						servicePeriod: Number(data.servicePeriod),
					},
					getAdditionalService: {
						pageNumber: 1,
						pageSize: PAGE_SIZE,
						filters: [],
						searchString: '',
						orderBy: [],
					},
				}),
			);
		} else if (idToEdit) {
			console.log('data', data);
			dispatch(
				updateAdditionalServiceRequest({
					id: idToEdit,
					data: {
						...data,
						price: Number(data.price),
					},
					getAdditionalService: {
						pageNumber: 1,
						pageSize: PAGE_SIZE,
						filters: [],
						searchString: '',
						orderBy: [],
					},
				}),
			);
		}
		handleEditClose();
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		switch (typeOfDialog) {
			case 'Edit':
				setIdToEdit(id);
				setIsEditDialogOpen(true);
				break;
			case 'Delete':
				setIdToDelete(id);
				setOpenConfirmationDialog(true);
				break;
			case 'Download': {
				const document = generalAttachments?.data?.find((item) => item.id === id);
				if (!document) break;

				if (document.versions.length === 1) {
					// If only one version, download immediately
					dispatch(
						downloadGeneralAttachmentRequest({
							attachmentId: document.versions[0].id,
							fileName: document.fileName,
						}),
					);
				} else {
					// If multiple versions, open dialog to select
					setDocumentToDownload(document);
					setIsDownloadDialogOpen(true);
				}
				break;
			}
			case 'Upload New Version':
				setIdToUpload(id);
				setIsUploadDialogOpen(true);
				break;
			default:
				break;
		}
	};

	const tableStructureToBoxStructure = (
		tableStruc: TTableStructure<keyof TAdditionalService>[],
	) => {
		const boxStructure: TBoxStructure<TAdditionalService> = {
			body: [],
			header: [],
			title: idToEdit ? 'Edit Additional Service' : 'Add Additional Service',
		};
		tableStruc.forEach((item: TTableStructure<keyof TAdditionalService>) => {
			if (item?.editable === true && item.selector !== 'actions') {
				boxStructure.body.push({
					name: item.name,
					selector: item.selector,
					type: item.type,
					editable: true,
				});
			}
		});
		return boxStructure;
	};

	return (
		<Box sx={{ margin: '5px' }}>
			<EasTableWithoutFilter
				tableStructure={tableStructure}
				items={generalAttachments}
				itemsIsLoading={generalAttachmentsLoading}
				handleAction={handleAction}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / <b>General Contract Documents</b>
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => setIsAddDialogOpen(true)}
					>
						Add Document
					</Button>
				</Box>
			</EasTableWithoutFilter>
			{/* <EditParamsDialog<TGeneralAttachmentRow>
				open={isEditDialogOpen}
				setClose={handleEditClose}
				data={generalAttachments?.data?.find(item => item.id === idToEdit) ?? null}
				boxStructure={tableStructureToBoxStructure(tableStructure)}
				generalAttachments={generalAttachments?.data ?? null}
				title={idToEdit ? 'Edit Additional Service' : 'Add Additional Service'}
				onSave={handleSave}
			/> */}
			<AddDocumentDialog
				open={isAddDialogOpen}
				onClose={handleAddDialogClose}
				onSave={handleAddDocument}
			/>
			<ConfirmationDialog
				open={openConfirmationDialog}
				setClose={setCloseConfirmationDialog}
				handleConfirm={handleConfirmationDialog}
				title="Delete Document"
				text="Are you sure you want to delete this document?"
			/>
			<UploadVersionDialog
				open={isUploadDialogOpen}
				attachmentId={idToUpload}
				onClose={handleUploadDialogClose}
				onSave={handleUploadVersion}
			/>
			<DownloadDocumentDialog
				open={isDownloadDialogOpen}
				fileName={documentToDownload?.fileName ?? ''}
				versions={documentToDownload?.versions ?? []}
				onClose={handleDownloadDialogClose}
				onDownload={handleDownloadVersion}
			/>
		</Box>
	);
};
